import 'src/i18n';

import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import i18n from 'i18next';

import { Country, TvDashboard, TvDevice } from 'src/api/zrm';
import LiveScreen, { isLiveDashboard } from 'src/tv/live/LiveScreen';
import { readTvToken } from 'src/tv/tvClient';
import TvPairedScreen from 'src/tv/TvPairedScreen';
import TvPairingScreen from 'src/tv/TvPairingScreen';
import { createTvQueryClient } from 'src/tv/tvQueries';
import { tvTheme } from 'src/tv/tvTheme';
import { getLanguage } from 'src/utils/getLocale';

interface TvAppProps {
  dashboard: TvDashboard;
}

/**
 * A wall screen at `/mb-dash`, `/kh-dash`, `/calls-map`, `/web-traffic` or `/live-map`. Rendered by `src/index.tsx`
 * instead of the agent app, outside Auth0 and every provider: the screen authenticates with its own `X-TV-Token` and
 * polls (or, on a live screen, streams) through its own QueryClient.
 */
const TvApp: FC<TvAppProps> = (props) => {
  const { dashboard } = props;

  // Held in memory, not re-read from storage: storage may be blocked, and then only this copy exists.
  const [token, setToken] = useState(() => readTvToken(dashboard));
  const [device, setDevice] = useState<TvDevice | null>(null);
  // The screen's own, in-memory QueryClient, made once (see `tvQueries.ts`).
  const [queryClient] = useState(createTvQueryClient);

  const handlePaired = useCallback((paired: TvDevice, pairedToken: string) => {
    setDevice(paired);
    setToken(pairedToken);
  }, []);

  const handleUnpaired = useCallback(() => {
    setDevice(null);
    setToken(null);
  }, []);

  // Before pairing only the host hints at the office; once paired the screen's country decides.
  const country = device?.country ?? (window.location.hostname.endsWith('zensum.no') ? Country.NO : Country.SE);

  useEffect(() => {
    const language = getLanguage(country);

    if (i18n.language !== language) i18n.changeLanguage(language);

    document.documentElement.setAttribute('lang', language);
  }, [country]);

  let screen = (
    <TvPairingScreen
      dashboard={dashboard}
      onPaired={handlePaired}
    />
  );

  // The live screens stream over their own WebSocket instead of polling a snapshot.
  if (token && isLiveDashboard(dashboard))
    screen = (
      <LiveScreen
        dashboard={dashboard}
        token={token}
        country={country}
        device={device}
        onDevice={setDevice}
        onUnpaired={handleUnpaired}
      />
    );
  else if (token)
    screen = (
      <TvPairedScreen
        dashboard={dashboard}
        token={token}
        device={device}
        onDevice={setDevice}
        onUnpaired={handleUnpaired}
      />
    );

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={tvTheme}>
        <CssBaseline />
        {screen}
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default TvApp;
